import Table from '@/common/components/Table'
import { Mapper } from '@/common/services/mapper'
import { cn } from '@/common/utils/cn'
import { useCompanyGovernmentAllocationSummary } from '@/features/privateCompanies/hooks/useCompanyGovernmentAllocationSummary'
import {
  governmentAllocationSummaryCurrencyTableColumns,
  governmentAllocationSummaryQuantityTableColumns,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/constants/governmentAllocationSummaryTableColumns'
import { CompanyGovernmentAllocationSummaryTransformer } from '@/features/privateCompanies/services/companyGovernmentAllocationSummaryTransformer'
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { CompanyGovernmentAllocationSummaryData } from '@/features/privateCompanies/types/companyGovernmentAllocationSummary.type'
import { useEffect, useMemo, useState } from 'react'

type GovernmentAllocationSummaryProps = {
  companyGovernmentAllocation: CompanyGovernmentAllocationData
}

export const GovernmentAllocationSummary = ({ companyGovernmentAllocation }: GovernmentAllocationSummaryProps) => {
  const [categoriesIsOpen, setCategoriesIsOpen] = useState<Record<string, boolean>>({})

  const {
    data: rawSummary,
    isLoading,
  } = useCompanyGovernmentAllocationSummary<CompanyGovernmentAllocationSummaryData>({
    governmentAllocationId: companyGovernmentAllocation.id,
  })

  useEffect(() => {
    if (!rawSummary?.categories) return

    const initialOpen = Object.keys(rawSummary.categories).reduce<Record<string, boolean>>(
      (acc, categoryId) => {
        acc[categoryId] = false
        return acc
      },
      {}
    )

    setCategoriesIsOpen(initialOpen)
  }, [rawSummary?.categories?.[0]?.display_name])

  const handleToggleCategory = (categoryId: string) => {
    setCategoriesIsOpen((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }))
  }

  const rows = useMemo(() => {
    if (!rawSummary) return []

    return Mapper.mapWithTransformer({
      transformer: CompanyGovernmentAllocationSummaryTransformer.tableTransformer(
        companyGovernmentAllocation.controlled_by,
        categoriesIsOpen,
        handleToggleCategory
      ),
    })(rawSummary)
  }, [rawSummary, categoriesIsOpen])

  return (
    <Table
      className={cn(
        'min-h-[256px]',
        companyGovernmentAllocation.controlled_by === 'quantity'
          ? 'max-h-[calc(100vh-432px)]'
          : 'max-h-[calc(100vh-496px)]',
      )}
      columns={
        companyGovernmentAllocation.controlled_by === 'currency'
          ? governmentAllocationSummaryCurrencyTableColumns
          : governmentAllocationSummaryQuantityTableColumns
      }
      rows={rows || []}
      stripedRows={false}
      page={1}
      totalPages={1}
      isLoadingData={isLoading}
    />
  )
}
